import { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { GenNode } from '../types/carbon/models/GenNode'
import { CarbonSpec } from '../types/carbon/models/CarbonSpec'
import { PlatinumProperties2 } from '../types/carbon/models/PlatinumProperties2'
import { XSpecProperties } from '../types/carbon/models/XSpecProperties'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useLoading from './useLoading'
import useSnackbar from './useSnackbar'
import { processNodes, sortNodesRecursively } from '../utils/tree'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import {
  generateNodeId,
  convertVariableToCodeframe,
} from '../utils/axisHelpers'
import {
  API_BASE_URL,
} from '../constants/system'
import { withAndedFilter } from '../utils/filter'
import {
  getURLBasedVisibilitySettings,
  getDefaultTableViewSettings,
} from '../utils/tables'

export type SpecEditorDriver = 'carbon' | 'autocoder'

/**
 * The sessionStorage keys the spec editor caches state under for one table:
 * the edit draft (axes, filter, weight, case filter, settings) and the Run
 * Spec preview (data, spec, excel). Single source of truth — the hook below
 * and anything that needs to discard editor state (the dashboard's Edit Data
 * overlay on Cancel, #968 Obs 7) must build keys through here.
 */
export const specEditorSessionKeys = (customer: string, job: string, table: string) => {
  const base = `${customer}/${job}/${(table || 'new').replace(/\.cbt$/, '')}`
  return {
    data: `previewdata:${base}`,
    // The Quick Filter the preview data ran under (the Tables page writes it).
    dataFilter: `previewdatafilter:${base}`,
    spec: `previewspec:${base}`,
    excel: `previewexcel:${base}`,
    draft: `editdraft:${base}`,
  }
}

/**
 * Discards every piece of cached spec-editor state for one table — draft and
 * preview alike (#968 Obs 7: unsaved Edit Spec changes must not survive
 * Cancel; re-entering must always show the last saved state).
 */
export const clearSpecEditorSessionState = (customer: string, job: string, table: string) => {
  const keys = specEditorSessionKeys(customer, job, table)
  ;[keys.data, keys.dataFilter, keys.spec, keys.excel, keys.draft].forEach(key => {
    try {
      window.sessionStorage.removeItem(key)
    } catch (err) {
      console.warn('Failed to clear spec editor session state:', err)
    }
  })
}

export const useSpecEditor = ({
  customer,
  job,
  table,
  skipSyntax = false,
  selectedVariables = [],
  resetChecked = () => {},
  onClearPreview = () => {},
  onSavedSpec = async () => {},
  driver = 'carbon' as SpecEditorDriver,
  selectedLabels = {} as Record<string, string[]>,
  onNavigateToTables,
}: {
  customer: string,
  job: string,
  table: string,
  skipSyntax?: boolean,
  selectedVariables?: GenNode[],
  resetChecked?: () => void,
  onClearPreview?: () => void,
  onSavedSpec?: () => Promise<void>,
  driver?: SpecEditorDriver,
  selectedLabels?: Record<string, string[]>,
  /**
   * Where to go once a spec has been run or saved. Defaults to navigating to
   * the Tables page, which is what the /editspec route wants.
   *
   * The dashboard's Edit Data overlay (#968 Part B) passes its own handler:
   * inside a modal there is nowhere to navigate to, and leaving the page would
   * discard the dashboard being edited.
   */
  onNavigateToTables?: (params: Record<string, string>) => void,
}) => {
  const account = useAccount()
  const loading = useLoading()
  const snackbar = useSnackbar()

  const navigateToTables = (params: Record<string, string> = {}) => {
    if (onNavigateToTables) {
      onNavigateToTables(params)
      return
    }
    account.openPage('tables', params)
  }

  const [carbonSpec, setCarbonSpec] = useState<CarbonSpec>()
  const [sideNodes, setSideNodes] = useState<GenNode[]>([])
  const [topNodes, setTopNodes] = useState<GenNode[]>([])
  const [sideSyntax, setSideSyntax] = useState('')
  const [topSyntax, setTopSyntax] = useState('')
  const [filter, setFilter] = useState('')
  const [weight, setWeight] = useState('')
  const [caseFilter, setCaseFilter] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sideAxisError, setSideAxisError] = useState<string | null>(null)
  const [topAxisError, setTopAxisError] = useState<string | null>(null)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterTargetNode, setFilterTargetNode] = useState<GenNode | null>(null)
  const [previewData, setPreviewData] = useState<PlatinumData | null>(null)
  const [previewSpec, setPreviewSpec] = useState<CarbonSpec | null>(null)
  const [previewExcel, setPreviewExcel] = useState<string | null>(null)
  const [savingPreview, setSavingPreview] = useState(false)
  const [displayProperties, setDisplayProperties] = useState<PlatinumProperties2>({})
  const [specProperties, setSpecProperties] = useState<XSpecProperties>({
    initAsMissing: false,
    excludeNE: false,
    padHierarchics: false,
    arithOverStats: false,
    fullStats: false,
  })
  const sideSyntaxRequestId = useRef(0)
  const topSyntaxRequestId = useRef(0)
  const draftSaveTimerRef = useRef<NodeJS.Timeout | null>(null)

  const loadQueryKey = ['spec', {
    customer,
    job,
    table,
  }]

  // GET /spec now returns CarbonSpec directly (not SpecWithPropertiesResponse)
  const loadSpec = async (): Promise<CarbonSpec> => {
    try {
      const res = await axios.get<CarbonSpec>(`${API_BASE_URL}/tables/${customer}/${job}/spec?path=${encodeURIComponent(table)}`)
      return res.data
    } catch (error) {
      throw new Error(extractErrorMessage(error))
    }
  }

  const loadSpecQuery = useQuery({
    queryKey: loadQueryKey,
    queryFn: async (): Promise<CarbonSpec | null> => {
      if(!customer || !job || !table) return null
      if(table === 'new' || table === 'new.cbt') {
        return null
      }
      return await loadSpec()
    },
  })

  // The display properties come from the CarbonSpec itself
  const originalProperties = loadSpecQuery.data?.displayProperties ?? null
  // For backward compat, expose a CarbonSpec-shaped "data" object
  // The useEffect below consumes this to populate the editor state
  const data = loadSpecQuery.data || null

  // Build preview storage key for this table (matches EditSpec convention)
  const sessionStorageKeys = specEditorSessionKeys(customer, job, table)

  const getNodesFromSyntax = async (syntax: string): Promise<GenNode[]> => {
    try {
      const driverParam = driver === 'autocoder' ? '?storageDriver=autocoder' : ''
      const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/tonodes${driverParam}`
      const response = await axios.post<GenNode[]>(url, {syntax})
      const nodes = response.data || []

      const nodesWithID = processNodes(nodes, node => {
        node.id = generateNodeId()
        return node
      })

      const tabAxisNode = nodesWithID.find(node => node.type === 'TabAxis')
      const children = tabAxisNode?.children || []

      // Sort all codeframe children so bases appear at the top
      return sortNodesRecursively(children)
    } catch (error) {
      throw error
    }
  }

  const refreshAxisSyntax = async (nodes: GenNode[], axis: 'side' | 'top') => {
    const requestRef = axis === 'side' ? sideSyntaxRequestId : topSyntaxRequestId
    const setSyntax = axis === 'side' ? setSideSyntax : setTopSyntax
    const setError = axis === 'side' ? setSideAxisError : setTopAxisError
    const requestId = requestRef.current + 1
    requestRef.current = requestId

    if (!nodes || nodes.length === 0) {
      setSyntax('')
      setError(null)
      return
    }

    if (!customer || !job) {
      setSyntax('')
      setError(null)
      return
    }

    try {
      const driverParam = driver === 'autocoder' ? '?storageDriver=autocoder' : ''
      const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/fromnodes${driverParam}`
      const response = await axios.post<string>(url, nodes)
      if (requestRef.current === requestId) {
        const newSyntax = response.data || ''
        setSyntax(newSyntax)
        setError(null) // Clear error on success
        // Pass the new syntax directly to avoid stale state closure issue
        if (axis === 'side') {
          debouncedSaveDraft(newSyntax, undefined)
        } else {
          debouncedSaveDraft(undefined, newSyntax)
        }
      }
    } catch (error) {
      console.error(`Failed to convert ${axis} axis nodes to syntax string:`, error)
      if (requestRef.current === requestId) {
        setSyntax('')
        setError(`Failed to convert ${axis} axis to syntax: ${extractErrorMessage(error)}`)
      }
    }
  }

  const applySyntaxToAxis = async (syntax: string, axis: 'side' | 'top') => {
    const setError = axis === 'side' ? setSideAxisError : setTopAxisError

    if (!customer || !job) {
      const message = 'Customer or job is missing. Please select a table first.'
      setError(message)
      snackbar.error(message)
      throw new Error(message)
    }

    try {
      const nodes = await getNodesFromSyntax(syntax)
      if (axis === 'side') {
        setSideSyntax(syntax)
        handleSideAxisChange(nodes)
        setSideAxisError(null) // Clear error on success
      } else {
        setTopSyntax(syntax)
        handleTopAxisChange(nodes)
        setTopAxisError(null) // Clear error on success
      }
    } catch (error) {
      console.error(`Failed to convert syntax string for ${axis} axis:`, error)
      const errorMsg = `Failed to apply ${axis} axis syntax: ${extractErrorMessage(error)}`
      setError(errorMsg)
      snackbar.error(errorMsg)
      throw error
    }
  }

  const handleSideSyntaxApply = async (syntax: string) => {
    await applySyntaxToAxis(syntax, 'side')
  }

  const handleTopSyntaxApply = async (syntax: string) => {
    await applySyntaxToAxis(syntax, 'top')
  }

  const handleSideAxisChange = (nextNodes: GenNode[], options?: { skipSyntax?: boolean }) => {
    setSideNodes(nextNodes)
    // Clear error when nodes are manually changed (e.g., via tree operations)
    setSideAxisError(null)
    if (!options?.skipSyntax && !skipSyntax) {
      void refreshAxisSyntax(nextNodes, 'side')
    }
  }

  const handleTopAxisChange = (nextNodes: GenNode[], options?: { skipSyntax?: boolean }) => {
    setTopNodes(nextNodes)
    // Clear error when nodes are manually changed (e.g., via tree operations)
    setTopAxisError(null)
    if (!options?.skipSyntax && !skipSyntax) {
      void refreshAxisSyntax(nextNodes, 'top')
    }
  }

  // Build a CarbonSpec from the current editor state, using local displayProperties/specProperties
  const buildCarbonSpec = (): CarbonSpec => {
    return {
      top: topSyntax,
      side: sideSyntax,
      filter: filter,
      weight: weight,
      caseFilter: caseFilter,
      useFilter: !!filter,
      useWeight: !!weight,
      outputFormat: 'PlatinumData',
      displayProperties: {
        ...displayProperties,
        cells: {
          // Defaults for required boolean fields
          frequencies: false,
          columnPercents: true,
          rowPercents: false,
          percentSign: false,
          missingAsZero: false,
          missingAsBlank: false,
          zeroAsBlank: false,
          blankAsChar: false,
          percentsAsProportions: false,
          thousandsSeparator: false,
          showRedundant100: false,
          ...displayProperties?.cells,
        },
        significance: {
          // Defaults for required fields
          visible: false,
          type: 'ColumnGroups' as const,
          propStat: 'Z' as const,
          meanStat: 'T' as const,
          autoParam: false,
          letters: 'Column' as const,
          appendLetters: false,
          headcount: 'Weighted' as const,
          meanPoolVariance: false,
          propPooledEst: false,
          continuityCorr: false,
          skipBase30: false,
          skipCell5: false,
          lettersLong: false,
          oneTailed: false,
          hiLoTest: false,
          ...displayProperties?.significance,
        },
      },
      specProperties: specProperties,
    }
  }

  const onRunSpec = async () => {
    loading.setLoading(true)

    try {
      if (driver === 'autocoder') {
        // AutoCoder driver: navigate to Tables with URL params
        // The Tables page will call the API directly

        // Clear any existing sessionStorage preview data
        try {
          window.sessionStorage.removeItem(sessionStorageKeys.data)
          window.sessionStorage.removeItem(sessionStorageKeys.dataFilter)
          window.sessionStorage.removeItem(sessionStorageKeys.spec)
          window.sessionStorage.removeItem(sessionStorageKeys.excel)
        } catch {}

        snackbar.success('Opening AutoCoder table...')
        clearDraft()

        const urlVis = getURLBasedVisibilitySettings()
        const defaults = getDefaultTableViewSettings().visibility
        const visibility = { ...defaults, ...urlVis }

        // Build AutoCoder URL params
        const autoCoderParams: Record<string, string> = {
          storageDriver: 'autocoder',
          acTop: topSyntax,
          acSide: sideSyntax,
          frequency: String(visibility.frequency),
          column_percent: String(visibility.column_percent),
          row_percent: String(visibility.row_percent),
          significance: String(visibility.significance),
        }

        // Only add non-empty params
        if (filter) autoCoderParams.acFilter = filter
        if (weight) autoCoderParams.acWeight = weight
        if (Object.keys(selectedLabels).length > 0) {
          autoCoderParams.acLabels = encodeURIComponent(JSON.stringify(selectedLabels))
        }

        navigateToTables({ ...autoCoderParams, saved: 'false' })
        loading.setLoading(false)
        return // Skip the rest of the function
      } else {
        // Carbon driver: save CarbonSpec to sessionStorage only, no API call
        // Tables page will run the spec on-demand
        const spec = buildCarbonSpec()

        try {
          if (customer && job) {
            window.sessionStorage.setItem(sessionStorageKeys.spec, JSON.stringify(spec))
            // Clear stale preview data so Tables knows to run fresh
            window.sessionStorage.removeItem(sessionStorageKeys.data)
            window.sessionStorage.removeItem(sessionStorageKeys.dataFilter)
            window.sessionStorage.removeItem(sessionStorageKeys.excel)
          }
        } catch (storageErr) {
          console.warn('Failed to store preview spec in sessionStorage:', storageErr)
        }

        snackbar.success(`Spec saved — running on Tables page...`)

        // Clear draft since we now have a preview spec
        clearDraft()

        // saved: 'false' — this is a preview run, not a save. The Edit Data
        // overlay (#968 Part B) tells the two apart by this flag rather than
        // by whether `table` is truthy: running an existing table's spec
        // reports that same real path here, which looked identical to a
        // genuine save and closed the overlay before anything was written.
        navigateToTables({ table: table || 'new', saved: 'false' })
      }
    } catch (error) {
      console.error('Error saving spec:', error)
      snackbar.error(`Error saving spec: ${extractErrorMessage(error)}`)
      setErrorMessage(extractErrorMessage(error))
    } finally {
      loading.setLoading(false)
    }
  }

  const [saveDialogOpen, setSaveDialogOpen] = useState(false)

  const onSaveSpec = () => {
    if (savingPreview) return
    if (!customer || !job) {
      snackbar.error('Customer or job is missing. Please select a table first.')
      return
    }
    if (!table) {
      snackbar.error('Table path is missing. Please reopen the table and try again.')
      return
    }
    if (!previewSpec) {
      snackbar.error('Preview spec is missing. Re-run Edit Spec to save the changes.')
      clearPreviewHandler()
      return
    }
    if (!account.user?.session_info?.email) {
      snackbar.error('User email is unavailable. Please sign in again.')
      return
    }
    console.log('[SaveTableDialog] opening with table:', table, 'email:', account.user?.session_info?.email)
    setSaveDialogOpen(true)
  }

  const onCancelSave = () => {
    setSaveDialogOpen(false)
  }

  const onConfirmSave = async (result: { name: string; subtitle: string; overwrite: boolean; destination?: string; execFolder?: string; userFolder?: string }) => {
    setSaveDialogOpen(false)

    const userEmail = account.user?.session_info?.email
    if (!userEmail || !previewSpec) return

    let confirmedName = result.name.replace(/\.cbt$/, '')

    // Extract visibility settings from preview data URL params or defaults
    const urlVis = getURLBasedVisibilitySettings()
    const defaults = getDefaultTableViewSettings().visibility

    // AND the URL quick filter onto the spec's own so the saved table includes it
    const urlFilter = new URLSearchParams(window.location.search).get('filter') || ''
    const specToSave = withAndedFilter(previewSpec, urlFilter)

    loading.setLoading(true)
    setSavingPreview(true)
    try {
      const isExec = result.destination === 'exec'
      const requestBody = {
        carbonSpec: specToSave,
        name: `${confirmedName}.cbt`,
        sub: result.subtitle,
        visibility: { ...defaults, ...urlVis },
        destination: isExec ? 'exec' : 'user',
        execFolder: isExec ? (result.execFolder || '') : '',
        // Subfolder within Tables/User/<email> ('A/B'): the Save dialog's
        // folder (the edited table's own folder on overwrite), or the Edit
        // Data overlay's (#968 Part B: the dashboard's folder on a fork).
        userFolder: isExec ? '' : (result.userFolder || ''),
      }

      const url = `${API_BASE_URL}/tables/${customer}/${job}/spec/save?path=${encodeURIComponent(table)}`
      const resp = await axios.post<{ saved_path: string }>(url, requestBody)
      await onSavedSpec()
      clearPreviewHandler()

      const savedPath = resp.data.saved_path
      const successMsg = isExec ? 'Table saved to Exec' : result.overwrite ? 'Table saved' : 'Table saved as new'
      snackbar.success(successMsg)
      // saved: 'true' — a genuine save, distinct from a Run Spec navigation.
      // The Edit Data overlay only repoints and closes on this signal.
      navigateToTables({ table: savedPath, saved: 'true' })
    } catch (error) {
      console.error('Failed to save edited spec:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      snackbar.error(`Failed to save edited spec: ${message}`)
    } finally {
      loading.setLoading(false)
      setSavingPreview(false)
    }
  }

  /** Save current settings back to the same user table (overwrite). */
  const onSaveSettingsToTable = async () => {
    const userEmail = account.user?.session_info?.email
    if (!userEmail || !customer || !job || !table) return

    // Build the current spec with latest settings
    const spec = buildCarbonSpec()

    const tableName = table.split('/').pop()?.replace(/\.cbt$/, '') || ''
    if (!tableName) return

    const urlVis = getURLBasedVisibilitySettings()
    const defaults = getDefaultTableViewSettings().visibility

    const requestBody = {
      carbonSpec: spec,
      name: `${tableName}.cbt`,
      sub: '',
      visibility: { ...defaults, ...urlVis },
    }

    setSavingPreview(true)
    try {
      const url = `${API_BASE_URL}/tables/${customer}/${job}/spec/save?path=${encodeURIComponent(table)}`
      await axios.post<{ saved_path: string }>(url, requestBody)
      snackbar.success('Settings saved to table')
    } catch (error) {
      console.error('Failed to save settings:', error)
      const message = error instanceof Error ? error.message : 'Unknown error'
      snackbar.error(`Failed to save settings: ${message}`)
    } finally {
      setSavingPreview(false)
    }
  }

  const onLoadPreview = () => {
    if (!sessionStorageKeys.data && !sessionStorageKeys.spec && !sessionStorageKeys.excel) {
      setPreviewData(null)
      setPreviewSpec(null)
      setPreviewExcel(null)
      return
    }

    if (sessionStorageKeys.data) {
      try {
        const rawData = window.sessionStorage.getItem(sessionStorageKeys.data)
        if (rawData) {
          const parsedData: PlatinumData = JSON.parse(rawData)
          setPreviewData(parsedData)
        } else {
          setPreviewData(null)
        }
      } catch (err) {
        console.warn('Failed to read preview from sessionStorage:', err)
        setPreviewData(null)
      }
    } else {
      setPreviewData(null)
    }

    if (sessionStorageKeys.spec) {
      try {
        const rawSpec = window.sessionStorage.getItem(sessionStorageKeys.spec)
        if (rawSpec) {
          const parsedSpec: CarbonSpec = JSON.parse(rawSpec)
          setPreviewSpec(parsedSpec)
        } else {
          setPreviewSpec(null)
        }
      } catch (err) {
        console.warn('Failed to read preview spec from sessionStorage:', err)
        setPreviewSpec(null)
      }
    } else {
      setPreviewSpec(null)
    }

    if (sessionStorageKeys.excel) {
      try {
        const excelData = window.sessionStorage.getItem(sessionStorageKeys.excel)
        if (excelData) {
          setPreviewExcel(excelData)
        } else {
          setPreviewExcel(null)
        }
      } catch (err) {
        console.warn('Failed to read preview excel from sessionStorage:', err)
        setPreviewExcel(null)
      }
    } else {
      setPreviewExcel(null)
    }
  }

  // Click handler for the preview info banner
  const clearPreviewHandler = () => {
    if (sessionStorageKeys.data) {
      try {
        window.sessionStorage.removeItem(sessionStorageKeys.data)
        window.sessionStorage.removeItem(sessionStorageKeys.dataFilter)
      } catch (err) {
        console.warn('Failed to remove preview from sessionStorage:', err)
      }
    }
    if (sessionStorageKeys.spec) {
      try {
        window.sessionStorage.removeItem(sessionStorageKeys.spec)
      } catch (err) {
        console.warn('Failed to remove preview spec from sessionStorage:', err)
      }
    }
    if (sessionStorageKeys.excel) {
      try {
        window.sessionStorage.removeItem(sessionStorageKeys.excel)
      } catch (err) {
        console.warn('Failed to remove preview excel from sessionStorage:', err)
      }
    }
    setPreviewData(null)
    setPreviewSpec(null)
    setPreviewExcel(null)
    // Reset display/spec properties back to the loaded spec values
    setDisplayProperties(data?.displayProperties ?? {})
    setSpecProperties(data?.specProperties ?? {
      initAsMissing: false,
      excludeNE: false,
      padHierarchics: false,
      arithOverStats: false,
      fullStats: false,
    })
    onClearPreview()
    snackbar.info('Preview discarded')
  }

  // Draft management functions
  const saveDraft = (overrideSideSyntax?: string, overrideTopSyntax?: string) => {
    if (!customer || !job || !table) return

    const draftData = {
      sideSyntax: overrideSideSyntax !== undefined ? overrideSideSyntax : sideSyntax,
      topSyntax: overrideTopSyntax !== undefined ? overrideTopSyntax : topSyntax,
      filter,
      weight,
      caseFilter,
      displayProperties,
      specProperties,
      timestamp: Date.now(),
    }

    console.log('[EditSpec] Saving draft:', {
      table,
      usedOverrides: {
        sideSyntax: overrideSideSyntax !== undefined,
        topSyntax: overrideTopSyntax !== undefined,
      },
      syntaxLengths: {
        side: draftData.sideSyntax.length,
        top: draftData.topSyntax.length,
      }
    })

    try {
      window.sessionStorage.setItem(sessionStorageKeys.draft, JSON.stringify(draftData))
      console.log('[EditSpec] Draft saved successfully')
    } catch (err) {
      console.warn('[EditSpec] Failed to save draft to sessionStorage:', err)
    }
  }

  const loadDraft = () => {
    if (!sessionStorageKeys.draft) return null

    try {
      const rawDraft = window.sessionStorage.getItem(sessionStorageKeys.draft)
      if (!rawDraft) {
        console.log('[EditSpec] No draft found in session storage')
        return null
      }

      const draft = JSON.parse(rawDraft)

      // Check if draft is too old (>24 hours)
      const age = Date.now() - (draft.timestamp || 0)
      const maxAge = 24 * 60 * 60 * 1000 // 24 hours
      if (age > maxAge) {
        console.log('[EditSpec] Draft is too old, clearing it')
        clearDraft()
        return null
      }

      console.log('[EditSpec] Loaded draft from session storage:', {
        age: `${Math.round(age / 1000)}s ago`,
        syntaxLengths: {
          side: draft.sideSyntax?.length || 0,
          top: draft.topSyntax?.length || 0,
        }
      })

      return draft
    } catch (err) {
      console.warn('[EditSpec] Failed to load draft from sessionStorage:', err)
      return null
    }
  }

  const clearDraft = () => {
    if (!sessionStorageKeys.draft) return

    console.log('[EditSpec] Clearing draft from session storage')
    try {
      window.sessionStorage.removeItem(sessionStorageKeys.draft)
    } catch (err) {
      console.warn('[EditSpec] Failed to remove draft from sessionStorage:', err)
    }
  }

  const debouncedSaveDraft = (overrideSideSyntax?: string, overrideTopSyntax?: string) => {
    if (draftSaveTimerRef.current) {
      console.log('[EditSpec] Cancelling previous draft save timer')
      clearTimeout(draftSaveTimerRef.current)
    }
    console.log('[EditSpec] Setting draft save timer (500ms delay)')
    draftSaveTimerRef.current = setTimeout(() => {
      saveDraft(overrideSideSyntax, overrideTopSyntax)
    }, 500)
  }

  const handleDisplayPropertiesChange = (props: PlatinumProperties2) => {
    setDisplayProperties(props)
    debouncedSaveDraft()
  }

  const handleSpecPropertiesChange = (props: XSpecProperties) => {
    setSpecProperties(props)
    debouncedSaveDraft()
  }

  const handleFiltersChange = (updates: {
    filter?: string | null
    weight?: string | null
    caseFilter?: string | null
  }) => {
    if(updates.filter !== undefined) setFilter(updates.filter || '')
    if(updates.weight !== undefined) setWeight(updates.weight || '')
    if(updates.caseFilter !== undefined) {
      setCaseFilter(updates.caseFilter || '')
      // Keep specProperties in step: Carbon reads its caseFilter first, so a
      // stale or cleared copy there would run a case filter the user changed
      // or typed back (#1809).
      setSpecProperties(prev => prev ? { ...prev, caseFilter: updates.caseFilter || '' } : prev)
    }
    debouncedSaveDraft()
  }

  // Recursive helper to update a node at any depth in the tree
  const updateNodeRecursively = (arr: GenNode[], targetId: number, updatedNode: GenNode): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      // Found the target node at this level - replace it
      const copy = [...arr]
      copy[idx] = updatedNode
      return [copy, true]
    }

    // Not found at this level - search children recursively
    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, wasFound] = updateNodeRecursively(n.children, targetId, updatedNode)
        if (wasFound) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }

  const handleSaveFilter = (filterString: string) => {
    // If no target node, treat as global filter edit
    if (!filterTargetNode) {
      setFilter(filterString)
      return
    }

    // Check if we're editing an existing Filter node
    if (filterTargetNode.type === 'Filter') {
      // EDIT MODE: Update the existing Filter node's filter text
      const updatedFilterNode: GenNode = {
        ...filterTargetNode,
        value1: filterString,
        value2: filterString,
      }

      // Try updating in side axis first
      const [newSide, sideChanged] = updateNodeRecursively(sideNodes, filterTargetNode.id!, updatedFilterNode)
      let [newTop, topChanged] = [topNodes, false]
      if (!sideChanged) {
        [newTop, topChanged] = updateNodeRecursively(topNodes, filterTargetNode.id!, updatedFilterNode)
      }

      if (sideChanged) {
        handleSideAxisChange(newSide)
      } else if (topChanged) {
        handleTopAxisChange(newTop)
      }

      setFilterTargetNode(null)
      return
    }

    // CREATE MODE: Helper to wrap a node with a Filter node in-place, preserving order
    const wrapNodeWithFilter = (nodes: GenNode[], targetId: number): [GenNode[], boolean] => {
      const idx = nodes.findIndex(n => n.id === targetId)
      if (idx !== -1) {
        const original = nodes[idx]
        const filterId = generateNodeId()
        const wrappedChild: GenNode = {
          ...original,
          parentId: filterId,
          level: (original.level || 0) + 1,
        }
        const filterNode: GenNode = {
          type: 'Filter',
          id: filterId,
          parentId: original.parentId,
          isExpanded: true,
          isFolder: true,
          value1: filterString,
          value2: filterString,
          level: original.level || 0,
          anyChildren: true,
          children: [wrappedChild],
        }
        const next = [...nodes]
        next.splice(idx, 1, filterNode)
        return [next, true]
      }
      let changed = false
      const next = nodes.map(n => {
        if (n.children && n.children.length > 0) {
          const [childList, did] = wrapNodeWithFilter(n.children, targetId)
          if (did) {
            changed = true
            return { ...n, children: childList }
          }
        }
        return n
      })
      return [next, changed]
    }

    const applyToAxis = (axisNodes: GenNode[]): [GenNode[], boolean] => wrapNodeWithFilter(axisNodes, filterTargetNode.id!)

    // Try side axis first
    const [newSide, sideChanged] = applyToAxis(sideNodes)
    let [newTop, topChanged] = [topNodes, false]
    if (!sideChanged) {
      [newTop, topChanged] = applyToAxis(topNodes)
    }

    if (sideChanged) {
      handleSideAxisChange(newSide)
    } else if (topChanged) {
      handleTopAxisChange(newTop)
    }

    setFilterTargetNode(null)
  }

  const handleOpenFilterModal = (node?: GenNode) => {
    setFilterTargetNode(node || null)
    setFilterModalOpen(true)
  }

  const handleCloseFilterModal = () => {
    setFilterModalOpen(false)
  }

  const handleAddSelectedToSideAxis = () => {
    if (selectedVariables.length === 0) return

    const newCodeframes = selectedVariables.map(selectedItem => {
      // If it's already a CodeFrame, use it directly (just ensure proper structure)
      if (selectedItem.type === 'CodeFrame') {
        const newId = generateNodeId()
        return {
          ...selectedItem,
          id: newId,
          parentId: 0,
          level: 1,
          anyChildren: (selectedItem.children || []).length > 0,
          children: (selectedItem.children || []).map(code => ({
            ...code,
            id: generateNodeId(),
            parentId: newId,
            level: 2
          }))
        }
      }
      // If it's a Variable, convert it to a Codeframe
      return convertVariableToCodeframe(selectedItem, selectedItem.children || [])
    })

    const updatedNodes = [...sideNodes, ...newCodeframes]
    handleSideAxisChange(updatedNodes)
    resetChecked()
  }

  const handleAddSelectedToTopAxis = () => {
    if (selectedVariables.length === 0) return

    const newCodeframes = selectedVariables.map(selectedItem => {
      // If it's already a CodeFrame, use it directly (just ensure proper structure)
      if (selectedItem.type === 'CodeFrame') {
        const newId = generateNodeId()
        return {
          ...selectedItem,
          id: newId,
          parentId: 0,
          level: 1,
          anyChildren: (selectedItem.children || []).length > 0,
          children: (selectedItem.children || []).map(code => ({
            ...code,
            id: generateNodeId(),
            parentId: newId,
            level: 2
          }))
        }
      }
      // If it's a Variable, convert it to a Codeframe
      return convertVariableToCodeframe(selectedItem, selectedItem.children || [])
    })

    const updatedNodes = [...topNodes, ...newCodeframes]
    handleTopAxisChange(updatedNodes)
    resetChecked()
  }

  // When the CarbonSpec loads, populate the editor state
  // GET /spec returns CarbonSpec with syntax strings, so we convert to nodes for the editors
  useEffect(() => {
    if(!data) return

    setCarbonSpec(data)

    // Check if we should restore from draft
    const draft = loadDraft()

    if (draft) {
      // Restore display/spec properties from draft, falling back to server data
      setDisplayProperties(draft.displayProperties ?? data.displayProperties ?? {})
      setSpecProperties(draft.specProperties ?? data.specProperties ?? {
        initAsMissing: false,
        excludeNE: false,
        padHierarchics: false,
        arithOverStats: false,
        fullStats: false,
      })

      // Restore draft values
      setFilter(draft.filter || '')
      setWeight(draft.weight || '')
      setCaseFilter(draft.caseFilter || '')
      setSideSyntax(draft.sideSyntax || '')
      setTopSyntax(draft.topSyntax || '')

      // Convert syntax back to nodes for display
      if (draft.sideSyntax) {
        void getNodesFromSyntax(draft.sideSyntax).then(nodes => {
          handleSideAxisChange(nodes, { skipSyntax: true })
        }).catch(() => {
          // If conversion fails, fall back to spec data
          if (data.side) {
            void getNodesFromSyntax(data.side).then(nodes => {
              handleSideAxisChange(nodes, { skipSyntax: true })
            }).catch(() => {})
          }
        })
      } else if (data.side) {
        void getNodesFromSyntax(data.side).then(nodes => {
          handleSideAxisChange(nodes, { skipSyntax: true })
        }).catch(() => {})
      }

      if (draft.topSyntax) {
        void getNodesFromSyntax(draft.topSyntax).then(nodes => {
          handleTopAxisChange(nodes, { skipSyntax: true })
        }).catch(() => {
          // If conversion fails, fall back to spec data
          if (data.top) {
            void getNodesFromSyntax(data.top).then(nodes => {
              handleTopAxisChange(nodes, { skipSyntax: true })
            }).catch(() => {})
          }
        })
      } else if (data.top) {
        void getNodesFromSyntax(data.top).then(nodes => {
          handleTopAxisChange(nodes, { skipSyntax: true })
        }).catch(() => {})
      }
    } else {
      // No draft — check sessionStorage for a preview spec (saved by a previous Run Spec)
      let preview: CarbonSpec | null = null
      try {
        const rawPreview = window.sessionStorage.getItem(sessionStorageKeys.spec)
        if (rawPreview) {
          preview = JSON.parse(rawPreview)
        }
      } catch {}

      setDisplayProperties(preview?.displayProperties ?? data.displayProperties ?? {})
      setSpecProperties(preview?.specProperties ?? data.specProperties ?? {
        initAsMissing: false,
        excludeNE: false,
        padHierarchics: false,
        arithOverStats: false,
        fullStats: false,
      })

      // Restore ALL fields from preview spec if available, otherwise from API data
      const effectiveSide = preview?.side ?? data.side ?? ''
      const effectiveTop = preview?.top ?? data.top ?? ''
      const effectiveFilter = preview?.filter ?? data.filter ?? ''
      const effectiveWeight = preview?.weight ?? data.weight ?? ''
      const effectiveCaseFilter = preview?.specProperties?.caseFilter
        ?? data.specProperties?.caseFilter ?? data.caseFilter ?? ''

      setFilter(effectiveFilter)
      setWeight(effectiveWeight)
      setCaseFilter(effectiveCaseFilter)
      setSideSyntax(effectiveSide)
      setTopSyntax(effectiveTop)

      // Convert syntax strings to GenNodes for the axis editors
      if (effectiveSide) {
        void getNodesFromSyntax(effectiveSide).then(nodes => {
          handleSideAxisChange(nodes, { skipSyntax: true })
        }).catch(err => {
          console.warn('Failed to convert side syntax to nodes:', err)
        })
      }
      if (effectiveTop) {
        void getNodesFromSyntax(effectiveTop).then(nodes => {
          handleTopAxisChange(nodes, { skipSyntax: true })
        }).catch(err => {
          console.warn('Failed to convert top syntax to nodes:', err)
        })
      }
    }
  }, [
    data,
  ])

  // Load preview PlatinumData from sessionStorage whenever the key changes
  useEffect(() => {
    onLoadPreview()
  }, [sessionStorageKeys.data, sessionStorageKeys.spec, sessionStorageKeys.excel])

  return {
    // State values
    tableSpec: carbonSpec,
    carbonSpec,
    sideNodes,
    topNodes,
    sideSyntax,
    topSyntax,
    filter,
    weight,
    caseFilter,
    errorMessage,
    sideAxisError,
    topAxisError,
    filterModalOpen,
    filterTargetNode,
    previewData,
    previewSpec,
    previewExcel,
    savingPreview,
    originalProperties,
    displayProperties,
    specProperties,
    hasPreviewData: previewData ? true : false,
    hasPreviewSpec: previewSpec ? true : false,
    isLoading: loadSpecQuery.isPending,
    onClearPreview: clearPreviewHandler,
    onClearDraft: clearDraft,
    onLoadPreview,
    sessionStorageKeys,

    // State setters
    setFilter,
    setDisplayProperties: handleDisplayPropertiesChange,
    setSpecProperties: handleSpecPropertiesChange,

    // Save dialog
    saveDialogOpen,
    saveDialogTablePath: table,
    onCancelSave,
    onConfirmSave,
    onSaveSettingsToTable,

    // Event handlers
    onRunSpec,
    onSaveSpec,
    buildCarbonSpec,
    handleFiltersChange,
    handleSaveFilter,
    handleOpenFilterModal,
    handleCloseFilterModal,
    handleAddSelectedToSideAxis,
    handleAddSelectedToTopAxis,
    handleSideAxisChange,
    handleTopAxisChange,
    handleSideSyntaxApply,
    handleTopSyntaxApply,
  }
}

export default useSpecEditor
